/* eslint-disable react/display-name */
import { Space, Table, TableProps, Typography } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import styles from 'src/styles/layout.module.css';
import { useDispatch } from 'react-redux';
import { AppDispatch } from 'src/index';
import { fetchBillingByCompanyId } from 'src/features/companyBilling/companyBilling.state';
import { useCompanyBilling } from 'src/features/companyBilling/companyBilling.hooks';
import { CompanyBillingRecord } from 'src/features/companyBilling/companyBilling.types';
import { useCompanyById } from 'src/features/companies/companies.hooks';
import { Company } from 'src/features/companies/companies.types';

const CompanyBillingTab: React.FC = () => {
  const { companyId } = useParams() as { companyId: string };
  const dispatch: AppDispatch = useDispatch();
  const records = useCompanyBilling();
  const company = useCompanyById(companyId) as Company;

  const [selectedFilterRange, setFilterRange] = useState<[string?, string?]>([]);
  const currency = useMemo(() => {
    const { location } = company;
    if (location) {
      switch (location.currency.toLowerCase()) {
        case 'rub':
          return '\u20BD';
        default:
          return location.currency;
      }
    }
  }, [company]);
  const tableConfig = useMemo<TableProps<CompanyBillingRecord>>(
    () => ({
      dataSource: records,
      rowKey: values => values?.id,
      columns: [
        {
          title: 'Шлем',
          dataIndex: 'label',
          key: 'label',
          sorter: (a, b) => {
            if (a.id === '-1' || b.id === '-1') {
              return 0;
            }
            return compareString(a.label, b.label);
          },
          render: (value, record) => (
            <>
              <Space direction={'vertical'}>
                {record.isActive && record.id !== '-1' ? (
                  <Link to={`/companies/${companyId}/headsets/${record.id}/subscriptions`}>{value}</Link>
                ) : (
                  <Typography.Text strong={record.id === '-1'}>{value}</Typography.Text>
                )}
              </Space>
            </>
          ),
        },
        {
          title: 'Комната',
          dataIndex: 'roomLabel',
          key: 'roomLabel',
          align: 'right',
          sorter: (a, b) => {
            if (a.id === '-1' || b.id === '-1') {
              return 0;
            }
            return compareString(a.roomLabel, b.roomLabel);
          },
          render: (value, record) => (
            <Typography.Text strong={record.id === '-1'}>{record.id !== '-1' && `${value}`}</Typography.Text>
          ),
        },
        {
          title: 'Всего',
          dataIndex: 'totalSucceeded',
          key: 'totalSucceeded',
          align: 'right',
          sorter: (a, b) => {
            if (a.id === '-1' || b.id === '-1') {
              return 0;
            }
            return a.totalSucceeded - b.totalSucceeded;
          },
          render: (value, record) => (
            <Typography.Text strong={record.id === '-1'}>{`${record.totalSucceeded} ${currency}`}</Typography.Text>
          ),
        },
        {
          title: 'Картой по SMS/Email',
          dataIndex: 'paymentSystemSucceeded',
          key: 'paymentSystemSucceeded',
          align: 'right',
          sorter: (a, b) => {
            if (a.id === '-1' || b.id === '-1') {
              return 0;
            }
            return a.paymentSystemSucceeded - b.paymentSystemSucceeded;
          },
          render: (value, record) => (
            <Typography.Text strong={record.id === '-1'}>{`${value} ${currency}`}</Typography.Text>
          ),
        },
        {
          title: 'QR код',
          dataIndex: 'qrCodeSucceeded',
          key: 'qrCodeSucceeded',
          align: 'right',
          sorter: (a, b) => {
            if (a.id === '-1' || b.id === '-1') {
              return 0;
            }
            return a.qrCodeSucceeded - b.qrCodeSucceeded;
          },
          render: (value, record) => (
            <Typography.Text strong={record.id === '-1'}>{`${value} ${currency}`}</Typography.Text>
          ),
        },
        {
          title: 'Ресепшен',
          dataIndex: 'deskSucceeded',
          key: 'deskSucceeded',
          align: 'right',
          sorter: (a, b) => {
            if (a.id === '-1' || b.id === '-1') {
              return 0;
            }
            return a.deskSucceeded - b.deskSucceeded;
          },
          render: (value, record) => (
            <Typography.Text strong={record.id === '-1'}>{`${value} ${currency}`}</Typography.Text>
          ),
        },
        {
          title: 'VIP доступ',
          dataIndex: 'vipAccessSucceeded',
          key: 'vipAccessSucceeded',
          align: 'right',
          sorter: (a, b) => {
            if (a.id === '-1' || b.id === '-1') {
              return 0;
            }
            return a.vipAccessSucceeded - b.vipAccessSucceeded;
          },
          render: (value, record) => (
            <Typography.Text strong={record.id === '-1'}>{`${value} ${currency}`}</Typography.Text>
          ),
        },
      ],
    }),
    [currency, company, records]
  );
  const compareString = (a: string | null | undefined, b: string | null | undefined) => {
    const cmp = a || '-';
    return cmp.localeCompare(b || '-');
  };

  useEffect(() => {
    dispatch(fetchBillingByCompanyId({ companyId }));
  }, [companyId, dispatch]);
  useEffect(() => {
    const start = selectedFilterRange[0];
    const end = selectedFilterRange[1];

    if (start && end) {
      dispatch(
        fetchBillingByCompanyId({
          companyId,
          params: {
            start,
            end,
          },
        })
      );
    }
  }, [companyId, dispatch, selectedFilterRange]);

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <Typography.Title level={4}>Биллинг</Typography.Title>
      </div>
      <div className={styles.content}>
        <Table {...tableConfig} sticky />
      </div>
    </div>
  );
};

export default CompanyBillingTab;
